import React, { useState, useEffect, useCallback } from 'react';
import { fetchBankInstructions, createBankInstruction, updateBankInstruction, recordBankInstructionTrustDeposit, fetchConveyancingDetails } from '../api';
import { todayInGaborone } from '../utils/dates';

const INSTRUCTION_TYPES = [
  ['BOND_INSTRUCTION', 'Bond Instruction'],
  ['GUARANTEE_LETTER', 'Guarantee Letter'],
  ['CANCELLATION_FIGURES', 'Cancellation Figures'],
  ['SETTLEMENT_LETTER', 'Settlement Letter'],
  ['OTHER', 'Other Bank Correspondence'],
];

const STATUS_CONFIG = {
  RECEIVED:     { label: 'Received',         color: 'bg-gray-100 text-gray-700' },
  ACKNOWLEDGED: { label: 'Acknowledged',     color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS:  { label: 'In Progress',      color: 'bg-yellow-100 text-yellow-700' },
  COMPLETED:    { label: 'Completed',        color: 'bg-green-100 text-green-700' },
  QUERIED:      { label: 'Queried to Bank',  color: 'bg-orange-100 text-orange-700' },
};

const BLANK = {
  instruction_type: 'BOND_INSTRUCTION', status: 'RECEIVED',
  bank_name: '', bank_reference: '', received_date: '',
  conveyancing_detail: '',
  guarantee_amount: '', guarantee_expiry: '',
  cancellation_figure: '', cancellation_date: '',
  deposit_amount: '',
  body_text: '', notes: '',
};

export default function BankInstructionInbox({ authAxios, showFormMessage, conveyancingDetailId = null }) {
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [selected, setSelected] = useState(null);
  // D.14: "Record as Trust Deposit" — pre-filled but editable, matching the
  // C.8/C.9 pattern already used elsewhere in this session's Phase C/D work.
  const [depositModal, setDepositModal] = useState(null); // { instruction, amount }
  const [recordingDeposit, setRecordingDeposit] = useState(false);
  const [matters, setMatters] = useState([]);

  useEffect(() => {
    fetchConveyancingDetails(authAxios).then(setMatters).catch(() => setMatters([]));
  }, [authAxios]);

  const today = todayInGaborone();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBankInstructions(authAxios, conveyancingDetailId ? { conveyancing_detail: conveyancingDetailId } : {});
      setInstructions(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load bank instructions.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, showFormMessage, conveyancingDetailId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, received_date: today, conveyancing_detail: conveyancingDetailId || '' });
    setShowForm(true);
  };

  const openEdit = (ins) => {
    setEditing(ins);
    setForm({
      ...BLANK, ...ins,
      conveyancing_detail: ins.conveyancing_detail ?? '',
      deposit_amount: ins.deposit_amount ?? '',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ['guarantee_amount', 'cancellation_figure', 'deposit_amount', 'conveyancing_detail'].forEach(k => { if (!payload[k]) payload[k] = null; });
      ['guarantee_expiry', 'cancellation_date'].forEach(k => { if (!payload[k]) payload[k] = null; });

      if (editing) {
        const saved = await updateBankInstruction(authAxios, editing.id, payload);
        showFormMessage?.('success', 'Instruction updated.');
        // Keep the detail pane (and the next openEdit()'s seed data) in
        // sync with what was just saved — load() alone only refreshes the
        // list, not `selected`, which otherwise stays stale until the
        // instruction is manually reselected.
        setSelected(sel => (sel && sel.id === editing.id ? saved : sel));
      } else {
        await createBankInstruction(authAxios, payload);
        showFormMessage?.('success', 'Instruction created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openDepositModal = (ins) => {
    setDepositModal({ instruction: ins, amount: ins.deposit_amount || ins.guarantee_amount || '' });
  };

  const handleRecordDeposit = async (e) => {
    e.preventDefault();
    if (!depositModal) return;
    setRecordingDeposit(true);
    try {
      await recordBankInstructionTrustDeposit(authAxios, depositModal.instruction.id, depositModal.amount);
      showFormMessage?.('success', 'Trust deposit recorded.');
      setDepositModal(null);
      load();
      setSelected(sel => (sel && sel.id === depositModal.instruction.id ? { ...sel, trust_deposit_recorded: true } : sel));
    } catch (err) {
      showFormMessage?.('error', err?.response?.data?.detail || 'Failed to record trust deposit.');
    } finally {
      setRecordingDeposit(false);
    }
  };

  const filtered = instructions.filter(i => {
    if (filterStatus && i.status !== filterStatus) return false;
    if (filterBank && !i.bank_name.toLowerCase().includes(filterBank.toLowerCase())) return false;
    return true;
  });

  const banks = [...new Set(instructions.map(i => i.bank_name))].filter(Boolean);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900">{conveyancingDetailId ? 'Bank Instructions' : 'Bank Instruction Inbox'}</h2>
        <button onClick={openCreate}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700">
          + New Instruction
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
        </select>
        <select value={filterBank} onChange={e => setFilterBank(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Banks</option>
          {banks.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <span className="text-sm text-gray-500 self-center">{filtered.length} instruction(s)</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* List */}
        <div className="lg:col-span-2 space-y-2">
          {loading && <p className="text-sm text-gray-400">Loading…</p>}
          {filtered.map(ins => {
            const sc = STATUS_CONFIG[ins.status] ?? { label: ins.status, color: 'bg-gray-100 text-gray-700' };
            return (
              <button key={ins.id} onClick={() => setSelected(ins)}
                className={`w-full text-left border rounded-xl p-4 hover:shadow-sm transition-shadow ${selected?.id === ins.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs font-medium text-gray-500">{ins.bank_name}</div>
                    <div className="text-sm font-semibold text-gray-800 mt-0.5">
                      {INSTRUCTION_TYPES.find(([v]) => v === ins.instruction_type)?.[1] ?? ins.instruction_type}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${sc.color}`}>{sc.label}</span>
                </div>
                <div className="mt-1 text-xs text-gray-400">{ins.received_date}</div>
              </button>
            );
          })}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">No instructions found.</p>
          )}
        </div>

        {/* Detail pane */}
        <div className="lg:col-span-3">
          {selected ? (
            <div className="border border-gray-200 rounded-xl bg-white p-5 space-y-4 sticky top-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {INSTRUCTION_TYPES.find(([v]) => v === selected.instruction_type)?.[1]}
                  </div>
                  <div className="text-sm text-gray-500">{selected.bank_name} • Ref: {selected.bank_reference || '—'}</div>
                </div>
                <div className="flex gap-2">
                  {selected.status === 'COMPLETED' && !selected.trust_deposit_recorded && (
                    <button onClick={() => openDepositModal(selected)}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Record as Trust Deposit
                    </button>
                  )}
                  <button onClick={() => openEdit(selected)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">Edit</button>
                </div>
              </div>

              {selected.trust_deposit_recorded && (
                <div className="text-xs font-medium text-green-700 bg-green-50 rounded-lg px-3 py-2">
                  Trust deposit recorded for this instruction.
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2"><span className="text-gray-500">Matter:</span> <span className="font-medium">
                  {selected.conveyancing_detail
                    ? (matters.find(m => m.id === selected.conveyancing_detail)
                        ? `${matters.find(m => m.id === selected.conveyancing_detail).case_number} — ${matters.find(m => m.id === selected.conveyancing_detail).case_title}`
                        : `Matter #${selected.conveyancing_detail}`)
                    : <span className="text-gray-400 italic">Not linked — use Edit to link a matter</span>}
                </span></div>
                <div><span className="text-gray-500">Received:</span> <span className="font-medium">{selected.received_date}</span></div>
                <div><span className="text-gray-500">Status:</span>
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${STATUS_CONFIG[selected.status]?.color}`}>
                    {STATUS_CONFIG[selected.status]?.label}
                  </span>
                </div>
                {selected.guarantee_amount && (
                  <div><span className="text-gray-500">Guarantee:</span> <span className="font-medium">BWP {parseFloat(selected.guarantee_amount).toLocaleString()}</span></div>
                )}
                {selected.guarantee_expiry && (
                  <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{selected.guarantee_expiry}</span>
                    {selected.guarantee_expiry_days !== undefined && selected.guarantee_expiry_days <= 30 && (
                      <span className="ml-1 text-red-500 text-xs">({selected.guarantee_expiry_days}d)</span>
                    )}
                  </div>
                )}
                {selected.cancellation_figure && (
                  <div><span className="text-gray-500">Cancellation Fig.:</span> <span className="font-medium">BWP {parseFloat(selected.cancellation_figure).toLocaleString()}</span></div>
                )}
                {selected.cancellation_date && (
                  <div><span className="text-gray-500">Cancel Date:</span> <span className="font-medium">{selected.cancellation_date}</span></div>
                )}
                {selected.deposit_amount && (
                  <div><span className="text-gray-500">Trust Deposit:</span> <span className="font-medium">BWP {parseFloat(selected.deposit_amount).toLocaleString()}</span></div>
                )}
              </div>

              {selected.body_text && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1 uppercase">Email Body</div>
                  <pre className="text-xs bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap text-gray-700 max-h-48">
                    {selected.body_text}
                  </pre>
                </div>
              )}
              {selected.notes && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1 uppercase">Notes</div>
                  <p className="text-sm text-gray-700">{selected.notes}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              Select an instruction to view details
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editing ? 'Edit Instruction' : 'New Bank Instruction'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSave} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Instruction Type *</label>
                <select required value={form.instruction_type} onChange={e => fld('instruction_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {INSTRUCTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => fld('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(STATUS_CONFIG).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bank Name *</label>
                <input required value={form.bank_name} onChange={e => fld('bank_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Bank Reference</label>
                <input value={form.bank_reference} onChange={e => fld('bank_reference', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Received Date *</label>
                <input required type="date" value={form.received_date} onChange={e => fld('received_date', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              {conveyancingDetailId ? (
                <div className="md:col-span-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  Linked to this matter automatically.
                </div>
              ) : (
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Link to Matter</label>
                  <select value={form.conveyancing_detail} onChange={e => fld('conveyancing_detail', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">— Not linked yet —</option>
                    {matters.map(m => (
                      <option key={m.id} value={m.id}>{m.case_number} — {m.case_title}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Required before "Record as Trust Deposit" can be used on this instruction.</p>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Guarantee Amount (BWP)</label>
                <input type="number" step="0.01" value={form.guarantee_amount} onChange={e => fld('guarantee_amount', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Guarantee Expiry</label>
                <input type="date" value={form.guarantee_expiry} onChange={e => fld('guarantee_expiry', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cancellation Figure (BWP)</label>
                <input type="number" step="0.01" value={form.cancellation_figure} onChange={e => fld('cancellation_figure', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Cancellation Date</label>
                <input type="date" value={form.cancellation_date} onChange={e => fld('cancellation_date', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Trust Deposit Amount (BWP)</label>
                <input type="number" step="0.01" value={form.deposit_amount} onChange={e => fld('deposit_amount', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-gray-400 mt-1">Optional — no BankInstruction type has one canonical amount field (e.g. Bond Instructions have neither guarantee_amount nor cancellation_figure). Pre-fills "Record as Trust Deposit" once Completed.</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Email Body / Correspondence</label>
                <textarea value={form.body_text} onChange={e => fld('body_text', e.target.value)}
                  rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Internal Notes</label>
                <textarea value={form.notes} onChange={e => fld('notes', e.target.value)}
                  rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {depositModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">Record as Trust Deposit</h3>
              <button onClick={() => setDepositModal(null)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleRecordDeposit} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                {depositModal.instruction.bank_name} — {INSTRUCTION_TYPES.find(([v]) => v === depositModal.instruction.instruction_type)?.[1]}
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount Deposited (BWP) *</label>
                <input required type="number" step="0.01" autoFocus
                  value={depositModal.amount}
                  onChange={e => setDepositModal(m => ({ ...m, amount: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <p className="text-xs text-gray-400 mt-1">Confirm the real amount banked — this may differ from the figure captured when the instruction was logged.</p>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setDepositModal(null)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={recordingDeposit}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {recordingDeposit ? 'Recording…' : 'Record Deposit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
